import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface VarianceAnalysisChartProps {
  data: {
    baseYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    selectedYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    baseYearValue: number;
    selectedYearValue: number;
  } | undefined;
}

export function VarianceAnalysisChart({ data }: VarianceAnalysisChartProps) {
  if (!data) return null;

  const chartData = [
    {
      name: 'Scope 1',
      variance: data.selectedYear.scope1 - data.baseYear.scope1,
      variancePercent: data.baseYear.scope1 > 0 ? ((data.selectedYear.scope1 - data.baseYear.scope1) / data.baseYear.scope1) * 100 : 0,
      baseValue: data.baseYear.scope1,
      selectedValue: data.selectedYear.scope1,
      fill: (data.selectedYear.scope1 - data.baseYear.scope1) >= 0 ? '#ef4444' : '#10b981',
    },
    {
      name: 'Scope 2',
      variance: data.selectedYear.scope2 - data.baseYear.scope2,
      variancePercent: data.baseYear.scope2 > 0 ? ((data.selectedYear.scope2 - data.baseYear.scope2) / data.baseYear.scope2) * 100 : 0,
      baseValue: data.baseYear.scope2,
      selectedValue: data.selectedYear.scope2,
      fill: (data.selectedYear.scope2 - data.baseYear.scope2) >= 0 ? '#ef4444' : '#10b981',
    },
    {
      name: 'Scope 3',
      variance: data.selectedYear.scope3 - data.baseYear.scope3,
      variancePercent: data.baseYear.scope3 > 0 ? ((data.selectedYear.scope3 - data.baseYear.scope3) / data.baseYear.scope3) * 100 : 0,
      baseValue: data.baseYear.scope3,
      selectedValue: data.selectedYear.scope3,
      fill: (data.selectedYear.scope3 - data.baseYear.scope3) >= 0 ? '#ef4444' : '#10b981',
    },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isPositive = data.variance >= 0;
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium mb-2">{label} Variance Analysis</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Base Year ({data.baseYearValue}):</span>
              <span className="font-medium">{data.baseValue.toFixed(2)} kg CO2e</span>
            </div>
            <div className="flex justify-between">
              <span>Selected Year ({data.selectedYearValue}):</span>
              <span className="font-medium">{data.selectedValue.toFixed(2)} kg CO2e</span>
            </div>
            <div className="pt-1 border-t">
              <div className="flex justify-between">
                <span className="font-medium">Absolute Change:</span>
                <span className={`font-medium ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
                  {isPositive ? '+' : ''}{data.variance.toFixed(2)} kg CO2e
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Percentage Change:</span>
                <span className={`font-medium ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
                  {isPositive ? '+' : ''}{data.variancePercent.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="pt-1 border-t">
              <div className={`text-xs ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
                {isPositive ? '📈 Increase from baseline' : '📉 Decrease from baseline'}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-w-0">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{
            top: 16,
            right: 16,
            left: 0,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis width={48} tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />
          <Bar 
            dataKey="variance" 
            fill="#8884d8"
            name="Variance (kg CO2e)"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}



